// v3.15-1131 — Transfer Call Modal.
//
// Blind-transfer the currently-active outbound call to another internal Hub
// user. Searchable directory (name / extension / department). Status badge
// per user (🟢 Available, 🟡 Busy, 🔴 Offline). Calls POST /api/telephony/
// transfer which routes via extension when available, falling back to the
// user's E.164 phone — fallback is automatic, no manual intervention.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, PhoneForwarded, Search, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import GhostModalHeader from './GhostModalHeader';

const STATUS_DOT = {
  available: 'bg-emerald-500',
  busy:      'bg-amber-500',
  offline:   'bg-rose-500',
  unknown:   'bg-slate-300',
};
const STATUS_BORDER = {
  available: 'border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50',
  busy:      'border-amber-200 hover:border-amber-400 hover:bg-amber-50',
  offline:   'border-rose-200 opacity-70',
  unknown:   'border-slate-200 hover:border-slate-300',
};

export default function TransferCallModal({ isOpen, onClose, activeCall, onTransferred }) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transferringId, setTransferringId] = useState(null);
  const searchRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTransferringId(null);
      // tiny delay so the input is in DOM when we focus
      setTimeout(() => { try { searchRef.current?.focus(); } catch { /* noop */ } }, 80);
    }
  }, [isOpen]);

  // Fetch targets — debounced via query change
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/telephony/transfer-targets', { params: { q: query } });
        if (!cancelled) setItems(res.data?.items || []);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load directory');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [isOpen, query]);

  const handleTransfer = async (target) => {
    if (!activeCall?.call_log_id) {
      toast.error('No active call to transfer.');
      return;
    }
    if (!target.has_phone) {
      toast.error(`${target.name} has no phone configured.`);
      return;
    }
    setTransferringId(target.id);
    try {
      const res = await api.post('/telephony/transfer', {
        call_log_id: activeCall.call_log_id,
        target_user_id: target.id,
      });
      const methodLbl = res.data?.transfer_method === 'phone_fallback'
        ? 'phone (auto-fallback)'
        : res.data?.transfer_method || 'phone';
      toast.success(`Transferred to ${target.name} via ${methodLbl}`);
      onTransferred?.(res.data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Transfer failed';
      toast.error(`Transfer failed: ${detail}`);
    } finally {
      setTransferringId(null);
    }
  };

  const visible = useMemo(() => items, [items]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="transfer-call-modal">
        <GhostModalHeader title="Transfer Call" subtitle="Blind transfer — select a teammate" icon={<PhoneForwarded className="h-5 w-5" />} />

        {/* Active call context strip */}
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
          {activeCall?.call_log_id ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="inline-flex w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-semibold text-slate-800">Active call:</span>
              <span className="text-slate-700">
                {activeCall.client_name || 'Unknown contact'}
                {activeCall.client_number ? ` · ${activeCall.client_number}` : ''}
              </span>
              <span className="text-[11px] text-slate-500 ml-auto">id {activeCall.call_log_id.slice(0, 8)}</span>
            </div>
          ) : (
            <div className="text-sm text-amber-700 flex items-center gap-2">
              <X className="h-4 w-4" />
              No active call detected — start a call from the dial pad first.
            </div>
          )}
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, extension, or department…"
              className="pl-9"
              data-testid="transfer-search-input"
            />
          </div>
        </div>

        {/* Directory list */}
        <div className="max-h-[420px] overflow-y-auto p-3 space-y-1.5" data-testid="transfer-directory">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400 text-xs">
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Loading directory…
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm" data-testid="transfer-empty">
              No teammates match that search.
            </div>
          ) : visible.map((t) => {
            const disabled = !t.has_phone || transferringId === t.id || !activeCall?.call_log_id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleTransfer(t)}
                disabled={disabled}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-white transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed ${STATUS_BORDER[t.status] || STATUS_BORDER.unknown}`}
                data-testid={`transfer-target-${t.id}`}
              >
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center">
                    {(t.name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${STATUS_DOT[t.status] || STATUS_DOT.unknown}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold text-slate-900 truncate">{t.name}</div>
                    {t.extension ? (
                      <span className="text-[11px] font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">ext {t.extension}</span>
                    ) : null}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {t.department}
                    {t.has_phone ? '' : ' · ⚠ no phone'}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                    t.status === 'available' ? 'text-emerald-700 bg-emerald-50' :
                    t.status === 'busy'      ? 'text-amber-700 bg-amber-50' :
                    t.status === 'offline'   ? 'text-rose-700 bg-rose-50' :
                                               'text-slate-700 bg-slate-100'
                  }`}>
                    {t.status_label}
                  </span>
                  {transferringId === t.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  ) : (
                    <PhoneForwarded className="h-4 w-4 text-blue-600" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50">
          <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            Extension preferred · phone fallback automatic
          </div>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="transfer-cancel-btn">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
